<script setup lang="ts">
  import { ref } from 'vue';
  import { IconImage } from '@/shared/ui';

  interface Props {
    modelValue: string;
    tooltip: string;
    iconSrc: string | object;
    showColorSwatch?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    showColorSwatch: true,
  });

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
  }>();

  const inputRef = ref<HTMLInputElement | null>(null);

  const handleClick = () => {
    inputRef.value?.click();
  };

  const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('update:modelValue', target.value);
  };
</script>

<template>
  <button
    v-tippy="{
      content: props.tooltip,
      placement: 'top',
      theme: 'custom',
      trigger: 'mouseenter',
      hideOnClick: true,
    }"
    type="button"
    class="color-picker-btn"
    :aria-label="props.tooltip"
    @click="handleClick"
  >
    <span class="color-picker-icon-wrap">
      <IconImage
        class="color-picker-main-icon"
        :src="props.iconSrc"
        :alt="props.tooltip"
      />
      <span
        v-if="showColorSwatch"
        class="color-swatch"
        :style="{ backgroundColor: modelValue }"
      />
    </span>
    <input
      ref="inputRef"
      type="color"
      class="color-picker-input"
      :value="modelValue"
      tabindex="-1"
      aria-hidden="true"
      @input="handleChange"
    >
  </button>
</template>

<style scoped>
  .color-picker-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 4px;
    color: var(--color-gray-03);
    transition: background-color 0.15s ease;
    flex-direction: column;
    gap: 2px;
  }

  .color-picker-btn:hover {
    background-color: var(--color-gray-01);
  }

  .color-picker-icon-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }

  .color-picker-main-icon :deep(svg) {
    width: 20px;
    height: 20px;
    overflow: visible;
  }

  .color-swatch {
    display: block;
    width: 16px;
    height: 3px;
    border-radius: 1px;
    border: 0.5px solid var(--color-gray-02);
  }

  .color-picker-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
  }
</style>
